import { useContext, useEffect, useState } from 'react';

import { EditOutlined, UserOutlined } from '@ant-design/icons';
import {
	Avatar,
	Button,
	Col,
	Drawer,
	Flex,
	Form,
	Input,
	Select,
	Spin,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type { SelectProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import { useCreateBatchProcess } from '@/api/batch_process';
import {
	useAddCreatorToMonitor,
	useGetCreatorByUrl,
	useGetCreatorsTags,
} from '@/api/monitor_collections';
import { WHISPER_ACTIONS } from '@/const';
import placeholderImage from '@/lib/assets/placeholder.jpg';
import isValidUrl from '@/lib/helpers/isValidUrl';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import Platforms from '@/pages/app/dashboard/creators/platforms';
import { PageContext } from '@/providers/PageContextProvider';

const layout = {
	labelCol: { span: 5 },
	wrapperCol: { span: 21 },
};

export default function AddNewCreatorDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);

	const [form] = useForm();
	const [searchUrl, setSearchUrl] = useState('');
	const [creator, setCreator] = useState<any>(null);
	const [tags, setTags] = useState<SelectProps['options']>([]);

	const {
		data: searchCreatorData,
		isFetching: isSearchCreatorFetching,
		isError: isSearchCreatorError,
		isSuccess: isSearchCreatorSuccess,
		error: searchError,
	} = useGetCreatorByUrl(isValidUrl(searchUrl) ? searchUrl : '');

	const { mutate: addCreatorToMonitor, isLoading: isAddToMonitorLoading } =
		useAddCreatorToMonitor();

	const { data: tagsData, isFetching: isTagsFetching } = useGetCreatorsTags({
		enabled: drawer?.type === 'add-new-creator' && drawer?.isOpen,
	});

	useEffect(() => {
		if (searchUrl && isSearchCreatorSuccess) {
			const creatorData = searchCreatorData?.data
				? searchCreatorData?.data
				: null;
			// console.log("creatorData", creatorData);

			if (creatorData) {
				setCreator(creatorData);
			} else {
				setCreator(null);
				setSearchUrl('');
				message.error('Creator not found');
			}
		}
		if (isSearchCreatorError) {
			const errMessage = (searchError as any)?.response?.data?.message;
			message.error(`An error occurred: ${errMessage}`);
			console.log('searchError', searchError);
		}
	}, [
		isSearchCreatorSuccess,
		isSearchCreatorError,
		searchUrl,
		searchCreatorData,
	]);

	const onCancelClick = () => {
		closeDrawer();
		form.resetFields();
		setSearchUrl('');
		setCreator(null);
	};

	const onSaveClick = async () => {
		// form.resetFields();
		// closeDrawer();

		const values = form.getFieldsValue();

		console.log(`values \n${JSON.stringify(values, null, 2)}`);

		try {
			await form.validateFields();

			addCreatorToMonitor(
				{
					id: drawer?.data?.id,
					creator_mongo_id: creator?._id || null,
					url: form.getFieldValue('link'),
					tags: form.getFieldValue('tags'),
				},
				{
					onSuccess: () => {
						message.success('Added');
						closeDrawer();
						form.resetFields();
						setTags([]);
						setSearchUrl('');
						setCreator(null);
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.log('error', error);
					},
				},
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handleChange = (value: string[]) => {
		setTags(value.map((item) => ({ label: item, value: item })));
	};

	const onBlurInput = (value: string) => {
		if (value?.trim()?.length > 0) {
			if (value?.trim() !== searchUrl) {
				setSearchUrl(value?.trim());
			}
		}
	};

	const tagsItems: any[] = tagsData?.data?.data?.items?.map((item: any) => ({
		label: item?.tag,
		value: item?.tag,
	}));

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'add-new-creator'}
			width={'650px'}
			onClose={onCancelClick}
			title="New creator"
		>
			<Col span={24}>
				<Flex
					justify="end"
					align="center"
					gap={20}
					style={{ marginBottom: '1.25rem' }}
				>
					<Button onClick={onCancelClick}>Cancel</Button>
					<Button
						type="primary"
						onClick={onSaveClick}
						loading={isAddToMonitorLoading}
					>
						Save
					</Button>
				</Flex>
			</Col>
			<Form
				layout="vertical"
				form={form}
				initialValues={{
					link: '',
					tags: [],
				}}
				// style={{ marginBottom: "1rem" }}
			>
				<Flex
					vertical
					// gap={16}
				>
					{/* {!searchUrl && !creator && ( */}
					<Form.Item
						label={'Creator link'}
						name="link"
						rules={[
							{
								required: true,
								message: 'Creator link is required',
							},
							{
								pattern:
									/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/gm,
								message:
									'Creator link must be valid URL (e.g. https://www.youtube.com/@...)',
							},
						]}
						// style={{ margin: 0 }}
					>
						<Input
							placeholder={'https://www.youtube.com/@...'}
							style={{ resize: 'none' }}
							onBlur={(e) => onBlurInput(e.target.value)}
							disabled={
								isSearchCreatorFetching ||
								(!!searchUrl && isValidUrl(searchUrl))
							}
							// value={searchUrl}
						/>
						{isSearchCreatorFetching && (
							<Flex
								justify="center"
								align="center"
								style={{
									position: 'absolute',
									right: '1rem',
									top: '50%',
									transform: 'translateY(-50%)',
								}}
							>
								<Spin size="small" />
							</Flex>
						)}
					</Form.Item>
					{/* )} */}

					{!isSearchCreatorFetching && searchUrl && creator?.profileName && (
						<Flex
							key={creator?.profileName || searchUrl}
							gap={8}
							align="center"
							style={{
								padding: '0.5rem',
								marginBottom: '1rem',
								border: '1px solid #ccc',
								borderRadius: '0.5rem',
							}}
						>
							<Avatar
								size={50}
								src={
									creator?.profileImageURL?.startsWith('http')
										? creator?.profileImageURL
										: prepareImageSrc(creator?.profileImageURL) ||
										  placeholderImage
								}
								icon={<UserOutlined />}
							></Avatar>
							<Typography.Title level={5} style={{ margin: 0 }}>
								{creator?.profileName}
							</Typography.Title>

							{creator?.platformHandles?.[0] && (
								<Flex
									align="center"
									justify="center"
									style={{ marginLeft: '1rem' }}
								>
									<Platforms platform={creator?.platformHandles?.[0]} />
								</Flex>
							)}

							<Tooltip title={'Edit'}>
								<Button
									type="dashed"
									// variant="outlined"
									style={{ marginLeft: 'auto' }}
									onClick={() => {
										setSearchUrl('');
										setCreator(null);
									}}
								>
									<EditOutlined />
								</Button>
							</Tooltip>
						</Flex>
					)}

					{!isSearchCreatorFetching &&
						searchUrl &&
						isValidUrl(searchUrl) &&
						!creator?.profileName && (
							<Flex
								gap={8}
								align="center"
								justify="center"
								style={{
									padding: '0.5rem',
									marginBottom: '1rem',
									border: '1px solid #ccc',
									borderRadius: '0.5rem',
								}}
							>
								<Typography.Title level={5} style={{ margin: 0 }}>
									{'Creator not found'}
								</Typography.Title>
								<Tooltip title={'Edit'}>
									<Button
										type="dashed"
										// variant="outlined"
										// style={{ marginLeft: "auto" }}
										onClick={() => {
											setSearchUrl('');
											setCreator(null);
										}}
									>
										<EditOutlined />
									</Button>
								</Tooltip>
							</Flex>
						)}

					<Form.Item
						label={'Search tags'}
						name="tags"
						// style={{ margin: 0 }}
					>
						<Select
							mode="tags"
							style={{ width: '100%' }}
							placeholder="Search tags"
							onChange={handleChange}
							options={tagsItems || []}
							disabled={isTagsFetching}
						/>
					</Form.Item>
				</Flex>
			</Form>
		</Drawer>
	);
}
